import { paramsString, promisedRequest } from './helpers.js'

import {
  parseLinkHeaderPagination,
  parseNotification,
  parseStatus,
} from 'src/services/entity_normalizer/entity_normalizer.service.js'
import { parseTagArgument } from 'src/services/tag_query/tag_query.js'

const MASTODON_USER_HOME_TIMELINE_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
  withMuted,
  onlyMedia,
  excludeVisibilities,
}) =>
  `/api/v1/timelines/home${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted, onlyMedia, excludeVisibilities })}`
const MASTODON_LIST_TIMELINE_URL = (
  id,
  { minId, sinceId, maxId, limit, replyVisibility, withMuted, onlyMedia },
) =>
  `/api/v1/timelines/list/${id}${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted, onlyMedia })}`
const MASTODON_DIRECT_MESSAGES_TIMELINE_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
  withMuted,
}) =>
  `/api/v1/timelines/direct${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted })}`
const MASTODON_PUBLIC_TIMELINE = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
  withMuted,
  local,
  remote,
  onlyMedia,
  instance,
}) =>
  `/api/v1/timelines/public${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted, local, remote, onlyMedia, instance })}`
// the tag argument may carry extra tags, see tag_query
const MASTODON_TAG_TIMELINE_URL = (
  tagArg,
  { minId, sinceId, maxId, limit, replyVisibility, withMuted, onlyMedia },
) => {
  const { tag, any, all, none } = parseTagArgument(tagArg)
  const extra = {
    any: any.length ? any : null,
    all: all.length ? all : null,
    none: none.length ? none : null,
  }
  return `/api/v1/timelines/tag/${encodeURIComponent(tag)}${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted, onlyMedia, ...extra })}`
}
export const MASTODON_USER_TIMELINE_URL = (
  id,
  {
    minId,
    sinceId,
    maxId,
    limit,
    replyVisibility,
    withMuted,
    pinned,
    onlyMedia,
    excludeReplies,
  },
) =>
  `/api/v1/accounts/${id}/statuses${paramsString({ minId, sinceId, maxId, limit, replyVisibility, withMuted, pinned, onlyMedia, excludeReplies })}`
const MASTODON_USER_FAVORITES_TIMELINE_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
}) =>
  `/api/v1/favourites${paramsString({ minId, sinceId, maxId, limit, replyVisibility })}`
const MASTODON_BOOKMARK_TIMELINE_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
  folderId,
}) =>
  `/api/v1/bookmarks${paramsString({ minId, sinceId, maxId, limit, replyVisibility, folderId })}`
// whether anything is bookmarked: asks for one bookmark
export const fetchAnyBookmark = ({ credentials } = {}) =>
  promisedRequest({
    url: MASTODON_BOOKMARK_TIMELINE_URL({ limit: 1 }),
    credentials,
  }).then(({ data }) => data.length > 0)
const PLEROMA_STATUS_QUOTES_URL = (
  id,
  { minId, sinceId, maxId, limit, replyVisibility },
) =>
  `/api/v1/pleroma/statuses/${id}/quotes${paramsString({ minId, sinceId, maxId, limit, replyVisibility })}`
const PLEROMA_USER_FAVORITES_TIMELINE_URL = (
  id,
  { minId, sinceId, maxId, limit, replyVisibility },
) =>
  `/api/v1/pleroma/accounts/${id}/favourites${paramsString({ minId, sinceId, maxId, limit, replyVisibility })}`
const AKKOMA_BUBBLE_TIMELINE_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  replyVisibility,
}) =>
  `/api/v1/timelines/bubble${paramsString({ minId, sinceId, maxId, limit, replyVisibility })}`

const MASTODON_USER_NOTIFICATIONS_URL = ({
  minId,
  sinceId,
  maxId,
  limit,
  includeTypes,
  accountId,
  replyVisibility,
  withMuted,
}) =>
  `/api/v1/notifications${paramsString({ minId, sinceId, maxId, limit, includeTypes, accountId, replyVisibility, withMuted })}`

export const fetchTimeline = ({
  timeline,
  credentials,
  sinceId,
  minId,
  maxId,
  userId,
  listId,
  statusId,
  tag,
  instance,
  accountId,
  withMuted,
  onlyMedia,
  excludeVisibilities,
  replyVisibility = 'all',
  includeTypes = [],
  bookmarkFolderId,
}) => {
  const timelineUrls = {
    friends: MASTODON_USER_HOME_TIMELINE_URL,
    public: MASTODON_PUBLIC_TIMELINE,
    publicAndExternal: MASTODON_PUBLIC_TIMELINE,
    instance: MASTODON_PUBLIC_TIMELINE,
    dms: MASTODON_DIRECT_MESSAGES_TIMELINE_URL,
    user: MASTODON_USER_TIMELINE_URL,
    userPinned: MASTODON_USER_TIMELINE_URL,
    // a profile's Posts: without the replies
    userPosts: MASTODON_USER_TIMELINE_URL,
    media: MASTODON_USER_TIMELINE_URL,
    list: MASTODON_LIST_TIMELINE_URL,
    favorites: MASTODON_USER_FAVORITES_TIMELINE_URL,
    publicFavorites: PLEROMA_USER_FAVORITES_TIMELINE_URL,
    bookmarks: MASTODON_BOOKMARK_TIMELINE_URL,
    bubble: AKKOMA_BUBBLE_TIMELINE_URL,
    tag: MASTODON_TAG_TIMELINE_URL,
    quotes: PLEROMA_STATUS_QUOTES_URL,

    notifications: MASTODON_USER_NOTIFICATIONS_URL,
  }
  // a profile's Favorites tab: that user's public favourites
  const kind = timeline === 'favorites' && userId ? 'publicFavorites' : timeline
  const urlFunc = timelineUrls[kind]

  const twoArgs = new Set([
    'user',
    'userPinned',
    'userPosts',
    'media',
    'list',
    'publicFavorites',
    'tag',
    'quotes',
  ])

  const params = {
    minId,
    sinceId,
    maxId,
    limit: 20,
  }

  const id = (() => {
    switch (kind) {
      case 'user':
      case 'userPinned':
      case 'userPosts':
      case 'media':
      case 'publicFavorites':
        return userId
      case 'list':
        return listId
      case 'quotes':
        return statusId
      case 'tag':
        return tag
    }
  })()

  const isNotifications = timeline === 'notifications'

  if (timeline === 'userPosts') {
    params.excludeReplies = true
  }
  if (timeline === 'media' || onlyMedia) {
    params.onlyMedia = true
  }
  if (excludeVisibilities?.length) {
    params.excludeVisibilities = excludeVisibilities
  }
  if (timeline === 'userPinned') {
    params.pinned = true
  }
  if (timeline === 'public') {
    params.local = true
  }
  if (timeline === 'instance') {
    params.instance = instance
  }
  if (timeline !== 'favorites' && timeline !== 'bookmarks') {
    params.withMuted = withMuted
  }
  if (replyVisibility !== 'all') {
    params.replyVisibility = replyVisibility
  }
  if (timeline === 'bookmarks' && bookmarkFolderId) {
    params.folderId = bookmarkFolderId
  }

  if (isNotifications && includeTypes.length > 0) {
    params.includeTypes = includeTypes
  }
  if (isNotifications && accountId) {
    params.accountId = accountId
  }

  const url = twoArgs.has(kind) ? urlFunc(id, params) : urlFunc(params)
  return promisedRequest({ url, credentials }).then((result) => {
    const pagination = parseLinkHeaderPagination(
      result.response.headers.get('Link'),
      {
        flakeId: timeline !== 'bookmarks' && timeline !== 'notifications',
      },
    )

    return {
      ...result,
      data: result.data.map(isNotifications ? parseNotification : parseStatus),
      pagination,
    }
  })
}
